"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import { useTheme } from "next-themes";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LocateFixed, Loader2 } from "lucide-react";

const DARK_STYLE_ID = "54ed8b64-0ea1-46d8-b5ea-60bc847bf5a5";

declare global {
  interface Window {
    naver: typeof naver;
  }
}

// 로드할 JSON 데이터의 타입 정의
interface TestCenter {
  name: string;
  lat: number | null;
  lng: number | null;
  roadAddress: string;
}

export default function TestCenterMapClient() {
  const [testCenters, setTestCenters] = useState<TestCenter[]>([]);
  const [query, setQuery] = useState("");
  const mapRef = useRef<naver.maps.Map | null>(null);
  const [map, setMap] = useState<naver.maps.Map | null>(null);
  const [markers, setMarkers] = useState<naver.maps.Marker[]>([]);
  const infoWindow = useRef<naver.maps.InfoWindow | null>(null);
  const activeMarker = useRef<naver.maps.Marker | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const YOUR_CLIENT_ID = "zne1tr7m7i"; // Naver Maps Client ID
  const { theme } = useTheme();

  // 테마 설정 (초기 렌더링 시 테마가 undefined일 수 있으므로 시스템 테마 확인)
  const getEffectiveTheme = useCallback(() => {
    if (theme === "dark") return true;
    if (theme === "light") return false;
    // theme이 undefined이거나 system인 경우 시스템 테마 확인
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  }, [theme]);

  // 1. 좌표가 포함된 JSON 파일을 불러옵니다.
  useEffect(() => {
    const fetchCoordsData = async () => {
      try {
        const response = await fetch(
          "/exam-registration/jcg-test-center/test-centers-with-coords.json"
        );
        const data: TestCenter[] = await response.json();
        setTestCenters(data);
        // console.log('Successfully loaded coordinate data:', data);
      } catch (error) {
        console.error("Failed to fetch coordinate data:", error);
      }
    };
    fetchCoordsData();
  }, []);

  // 2. Naver 지도 스크립트를 로드하고 지도를 초기화합니다.
  useEffect(() => {
    const initMap = () => {
      if (!window.naver || !window.naver.maps) {
        console.error("Naver Maps script failed to load.");
        return;
      }
      const container = document.getElementById("map");
      if (!container) {
        console.error("Map container not found.");
        return;
      }

      // 기존 지도가 있으면 제거
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
        setMap(null);
      }

      const isDark = getEffectiveTheme();
      const options: naver.maps.MapOptions = {
        center: new window.naver.maps.LatLng(37.5236, 126.898), // 초기 중심: 영등포
        zoom: 14,
        gl: true, // 벡터 지도 활성화 (다크모드 스타일 적용에 필수)
        ...(isDark && { customStyleId: DARK_STYLE_ID }),
      };
      const naverMap = new window.naver.maps.Map(container, options);
      mapRef.current = naverMap;
      setMap(naverMap);

      const infoBgColor = isDark ? "#1f2937" : "#ffffff";
      const infoBorderColor = isDark ? "#374151" : "#ccc";
      infoWindow.current = new window.naver.maps.InfoWindow({
        content: "",
        backgroundColor: infoBgColor,
        borderColor: infoBorderColor,
        anchorColor: infoBgColor,
        borderWidth: 1,
      });
    };

    // 스크립트가 이미 로드되어 있고 naver 객체가 준비되어 있으면 바로 초기화
    if (window.naver && window.naver.maps) {
      initMap();
      return;
    }

    // 스크립트가 아직 없으면 새로 추가
    const existingScript = document.querySelector(
      `script[src*="openapi.map.naver.com"]`
    );

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${YOUR_CLIENT_ID}&submodules=geocoder,gl`;
      script.async = true;
      script.onload = initMap;
      document.head.appendChild(script);
    } else {
      // 스크립트는 있지만 아직 로드 중인 경우, 로드 완료를 기다림
      existingScript.addEventListener("load", initMap);
    }

    return () => {
      // 스크립트는 제거하지 않음 (재사용을 위해)
    };
  }, [getEffectiveTheme]);

  // 3. 불러온 좌표 데이터로 마커를 생성합니다.
  useEffect(() => {
    if (!map || testCenters.length === 0) return;

    const newMarkers = testCenters
      .filter((center) => center.lat && center.lng) // 좌표가 있는 데이터만 필터링
      .map((center) => {
        const marker = new window.naver.maps.Marker({
          position: new window.naver.maps.LatLng(center.lat!, center.lng!),
          title: center.name,
          icon: {
            url: "/exam-registration/jcg-test-center/test-center_gamza.webp",
            size: new window.naver.maps.Size(100, 100),
            scaledSize: new window.naver.maps.Size(70, 70),
            anchor: new window.naver.maps.Point(25, 50),
          },
        });

        window.naver.maps.Event.addListener(marker, "click", () => {
          if (infoWindow.current) {
            if (activeMarker.current === marker) {
              infoWindow.current.close();
              activeMarker.current = null;
            } else {
              const isDark = getEffectiveTheme();
              const bgColor = isDark ? "#1f2937" : "#ffffff";
              const textColor = isDark ? "#ffffff" : "#1f2937";
              const borderColor = isDark ? "#374151" : "#ccc";
              infoWindow.current.setContent(
                `<div style="padding:10px;border:1px solid ${borderColor};border-radius:5px;background-color:${bgColor};color:${textColor};"><b>${center.name}</b><br>${center.roadAddress}</div>`
              );
              infoWindow.current.open(map, marker);
              activeMarker.current = marker;
            }
          }
        });

        return marker;
      });

    setMarkers(newMarkers);
  }, [map, testCenters, getEffectiveTheme]);

  // 4. 지도 경계에 따라 마커의 노출 여부를 결정합니다. (성능 최적화)
  useEffect(() => {
    if (!map || markers.length === 0) return;

    const updateMarkersVisibility = () => {
      const mapBounds = map.getBounds();
      markers.forEach((marker) => {
        const isVisible = (mapBounds as naver.maps.LatLngBounds).hasLatLng(
          marker.getPosition()
        );
        marker.setMap(isVisible ? map : null);
      });
    };

    const idleListener = window.naver.maps.Event.addListener(
      map,
      "idle",
      updateMarkersVisibility
    );
    updateMarkersVisibility(); // 초기 실행

    return () => {
      window.naver.maps.Event.removeListener(idleListener);
    };
  }, [map, markers]);

  // 검색 기능 (기존과 동일)
  const handleSearch = () => {
    if (!query || !map) return;

    window.naver.maps.Service.geocode(
      { query },
      (
        status: naver.maps.Service.Status,
        response: naver.maps.Service.GeocodeResponse
      ) => {
        if (status !== window.naver.maps.Service.Status.OK) {
          return alert("주소를 찾을 수 없습니다.");
        }
        const result = response.v2;
        const items = result.addresses;
        if (items.length > 0) {
          const { x, y } = items[0];
          const newCenter = new window.naver.maps.LatLng(Number(y), Number(x));
          map.setCenter(newCenter);
          map.setZoom(14);
        }
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 현재 위치로 지도 중심 이동
  const handleLocateMe = () => {
    if (!map) return;

    if (!navigator.geolocation) {
      alert("이 브라우저에서는 위치 기능을 지원하지 않습니다.");
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newCenter = new window.naver.maps.LatLng(latitude, longitude);
        map.setCenter(newCenter);
        map.setZoom(14);
        setIsLocating(false);
      },
      (error) => {
        setIsLocating(false);
        console.error("Geolocation error:", error);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            alert("위치 권한이 거부되었습니다. 브라우저 설정에서 위치 권한을 허용해주세요.");
            break;
          case error.POSITION_UNAVAILABLE:
            alert(
              "위치 정보를 사용할 수 없습니다.\n\n" +
                "macOS: 시스템 설정 → 개인정보 보호 및 보안 → 위치 서비스에서 브라우저 권한을 확인해주세요."
            );
            break;
          case error.TIMEOUT:
            alert("위치 요청 시간이 초과되었습니다.");
            break;
          default:
            alert("위치를 가져오는 중 오류가 발생했습니다.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  return (
    <>
      {/* UI 2.0 셸 톤 검색 바 — 앰버 액센트 + 라운드 + border-border + backdrop (JCG-2100) */}
      <div className="mx-auto flex w-full max-w-xl items-center gap-2 rounded-2xl border border-border bg-card/70 p-2 shadow-sm backdrop-blur-sm">
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            map ? "지역명(동,읍,면) 입력" : "지도를 불러오는 중…"
          }
          aria-label="시험장 지역 검색"
          enterKeyHint="search"
          disabled={!map}
          onKeyDown={handleKeyDown}
          className="h-10 flex-1 rounded-xl border-border bg-background focus-visible:ring-amber-500"
        />
        <Button
          onClick={handleSearch}
          disabled={!map}
          className="h-10 rounded-xl bg-amber-500 font-semibold text-white hover:bg-amber-600"
        >
          검색
        </Button>
        <Button
          onClick={handleLocateMe}
          disabled={!map || isLocating}
          variant="outline"
          size="icon"
          aria-label="내 위치로 이동"
          aria-busy={isLocating}
          title="내 위치로 이동"
          className="h-10 w-10 rounded-xl border-border"
        >
          {isLocating ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <LocateFixed className="h-4 w-4 text-amber-500" aria-hidden="true" />
          )}
        </Button>
      </div>
      <div
        id="map"
        role="application"
        aria-label="시험장 지도"
        className="mt-4 h-[500px] w-full overflow-hidden rounded-2xl border border-border"
      ></div>
    </>
  );
}
